import React, { useEffect, useRef, useState } from 'react';

const Cursor: React.FC = () => {
  const cursorRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);

  const mouse = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const ring = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

  const animationFrame = useRef<number>();

  useEffect(() => {
    const cursor = cursorRef.current;
    const ringEl = ringRef.current;
    if (!cursor || !ringEl) return;

    let running = true;

    const updateCursor = () => {
      cursor.style.transform = `translate3d(${mouse.current.x}px, ${mouse.current.y}px, 0) translate(-50%, -50%)`;
      ring.current.x += (mouse.current.x - ring.current.x) * 0.15;
      ring.current.y += (mouse.current.y - ring.current.y) * 0.15;
      ringEl.style.transform = `translate3d(${ring.current.x}px, ${ring.current.y}px, 0) translate(-50%, -50%)`;
    
      if (running) {
        animationFrame.current = requestAnimationFrame(updateCursor);
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    };

    const onMouseDown = () => setIsClicking(true);
    const onMouseUp = () => setIsClicking(false);

    const onMouseEnter = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isClickable =
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('a') ||
        target.closest('button') ||
        target.classList.contains('clickable');
      setIsHovering(!!isClickable);
    };

    document.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mouseup', onMouseUp);

    const clickableSelector = 'a, button, .clickable';
    let clickableElements = Array.from(document.querySelectorAll(clickableSelector));
    clickableElements.forEach(el => {
      el.addEventListener('mouseenter', (e: Event) => onMouseEnter(e as MouseEvent));
      el.addEventListener('mouseleave', () => setIsHovering(false));
    });

    document.body.style.cursor = 'none';
    cursor.style.opacity = '1';
    ringEl.style.opacity = '1';

    animationFrame.current = requestAnimationFrame(updateCursor);

    return () => {
      running = false;
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mouseup', onMouseUp);
      clickableElements.forEach(el => {
        el.removeEventListener('mouseenter', (e: Event) => onMouseEnter(e as MouseEvent));
        el.removeEventListener('mouseleave', () => setIsHovering(false));
      });
      document.body.style.cursor = '';
    };
  }, []);

  return (
    <>
      {/* Inner Neon Cursor */}
      <div
        ref={cursorRef}
        className={`fixed z-50 w-3 h-3 rounded-full pointer-events-none transition-transform duration-75 transform -translate-x-1/2 -translate-y-1/2
          ${isClicking ? 'scale-50' : 'scale-100'}
          bg-purple-500 shadow-[0_0_8px_2px_rgba(168,85,247,0.8)]
        `}
        style={{ opacity: 0 }}
      />

      {/* Outer Ring with Glow */}
      <div
        ref={ringRef}
        className={`fixed z-40 pointer-events-none transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 ease-out
          rounded-full border-[2px] 
          ${isHovering ? 'w-16 h-16 border-fuchsia-500 shadow-[0_0_30px_10px_rgba(255,0,255,0.4)]' : 'w-10 h-10 border-purple-600'}
          ${isClicking ? 'scale-90 opacity-80' : 'opacity-100'}
        `}
        style={{ opacity: 0 }}
      />
    </>
  );
};

export default Cursor;

